/**
 * Settings → Integrations' MCP servers (slice 3b spec §5.1, §6; Paper 40,
 * 40b, 44): a card per server the person added, with its state, its tools On
 * and Off, its Who can use it dropdown (slice 6a §6.4) and its menu (Tools, Refresh
 * tools, Reconnect, Remove), and Add by URL and Browse servers. Coming back
 * from a server's sign-in (`?connected=mcp:<name>`), its tools open once for
 * them to review.
 */
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@crew/ui/components/alert-dialog";
import { Button, buttonVariants } from "@crew/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@crew/ui/components/dropdown-menu";
import { Link } from "@crew/ui/components/link";
import { cn } from "@crew/ui/lib/utils";
import { ChevronRight, Ellipsis, Plus } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import {
  useReconnectServer,
  useRefreshServer,
  useRemoveServer,
  useShareServer,
  type McpServer,
} from "./integrations-api";
import { AppIcon, ErrorLine, SOLID_RED, StateLabel } from "./integrations-parts";
import { ShareSelect } from "./shared-section";
import {
  AUTH_WORDS,
  bareUrl,
  plural,
  serverGuideLine,
  serverLines,
  serverMenu,
  serverActionAllowed,
  serverButton,
  serverState,
  type ServerAction,
  type ServerLocal,
} from "./integrations-view";
import { AddServerWindow, NewKeyWindow, ServerToolsWindow } from "./mcp-windows";
import { openSignInTab } from "./signin-tab";

/** Which server window is open: Add by URL, a server's tools, or a new key. */
type ServerWindow =
  | { kind: "add" }
  | { kind: "tools"; server: McpServer }
  | { kind: "key"; server: McpServer }
  | null;

/**
 * The open window. Back from a server's sign-in (`review`), its tools open
 * once, until the person closes them (spec §6.2).
 */
function useServerWindow(review: string | null, servers: McpServer[]) {
  const [win, setWin] = useState<ServerWindow>(null);
  const [reviewed, setReviewed] = useState<string | null>(null);
  const pending =
    review && review !== reviewed ? (servers.find((x) => x.name === review) ?? null) : null;
  return {
    win,
    setWin,
    toolsFor: win?.kind === "tools" ? win.server : win === null ? pending : null,
    closeTools: () => {
      setWin(null);
      if (review) setReviewed(review);
    },
  };
}

/**
 * The person's own servers (slice 6a §6.4): their cards, the windows they
 * open (Add by URL, a server's tools, a new key), and `add`, for the Add by
 * URL button below the page's lists.
 */
export function useServers({
  servers,
  available,
  timeZone,
  now,
  review,
  onNotice,
}: {
  /** The person's own: everything here is theirs to change. */
  servers: McpServer[];
  available: boolean;
  timeZone: string;
  /** The server's time, for a sign-in's countdown. */
  now: number;
  /** A server just signed in (`?connected=mcp:<name>`): its tools open for review. */
  review: string | null;
  onNotice: (title: string) => void;
}) {
  const { win, setWin, toolsFor, closeTools } = useServerWindow(review, servers);
  const cards = servers.map((s) => (
    <ServerCard
      key={s.id}
      server={s}
      manage={available}
      timeZone={timeZone}
      now={now}
      onTools={() => setWin({ kind: "tools", server: s })}
      onNewKey={() => setWin({ kind: "key", server: s })}
      onNotice={onNotice}
    />
  ));
  const windows = (
    <>
      <AddServerWindow
        open={win?.kind === "add"}
        entry={null}
        onClose={() => setWin(null)}
        onAdded={(server) => setWin({ kind: "tools", server })}
      />
      <ServerToolsWindow server={toolsFor} owner timeZone={timeZone} onClose={closeTools} />
      <NewKeyWindow server={win?.kind === "key" ? win.server : null} onClose={() => setWin(null)} />
    </>
  );
  return { cards, windows, add: () => setWin({ kind: "add" }) };
}

/** Add by URL (Add MCP server, empty) and Browse servers (the directory) (spec §5.1). */
export function ServerButtons({ className, onAdd }: { className?: string; onAdd: () => void }) {
  return (
    <div className={cn("gap-2", className)}>
      <Button variant="outline" className="h-11 bg-card md:h-9" onClick={onAdd}>
        <Plus className="size-3" />
        Add by URL
      </Button>
      <Link
        href="/settings/integrations/apps?filter=mcp"
        className={cn(buttonVariants({ variant: "outline" }), "h-11 bg-card md:h-9")}
      >
        Browse servers
        <ChevronRight className="size-3" />
      </Link>
    </div>
  );
}

/** A card's actions: Tools, Refresh tools, Reconnect (a new key, or sign in again) and Remove. */
function useServerActions(
  s: McpServer,
  on: { onTools: () => void; onNewKey: () => void; onNotice: (title: string) => void },
) {
  const refresh = useRefreshServer();
  const reconnect = useReconnectServer();
  const remove = useRemoveServer();
  const share = useShareServer();
  const [confirm, setConfirm] = useState(false);
  const busy = refresh.isPending || reconnect.isPending || remove.isPending || share.isPending;
  // A sign-in link goes only to a card still on the page: not one removed meanwhile (Codex on #251).
  const shown = useRef(true);
  useEffect(() => {
    shown.current = true;
    return () => {
      shown.current = false;
    };
  }, []);
  const run = (a: ServerAction) => {
    // One change at a time per server: nothing starts while another runs (Codex on #251).
    if (!serverActionAllowed(a, busy)) return;
    if (a === "tools") on.onTools();
    else if (a === "refresh") refresh.mutate(s.id);
    else if (a === "remove") setConfirm(true);
    else if (s.auth === "api_key") on.onNewKey();
    // The sign-in's tab opens now, in the click, so no popup blocker stops it.
    else reconnect.mutate({ id: s.id, current: () => shown.current, tab: openSignInTab() });
  };
  return {
    run,
    confirm,
    setConfirm,
    reading: refresh.isPending,
    removing: remove.isPending,
    busy,
    error: refresh.error ?? reconnect.error ?? remove.error ?? share.error,
    doRemove: () => remove.mutate(s.id, { onSuccess: () => on.onNotice(`${s.name} is removed.`) }),
    doShare: (shared: boolean) =>
      share.mutate(
        { id: s.id, shared },
        {
          onSuccess: () =>
            on.onNotice(
              shared ? `${s.name} is shared with the crew.` : `${s.name} is just yours now.`,
            ),
        },
      ),
  };
}

/**
 * A server's card (spec §6.2; Paper 40, 44, 74): its state, tools On and
 * Off, its Who can use it dropdown and its menu. `manage`: apps reach something, so its
 * actions can run.
 */
function ServerCard({
  server: s,
  manage,
  timeZone,
  now,
  ...on
}: {
  server: McpServer;
  manage: boolean;
  timeZone: string;
  now: number;
  onTools: () => void;
  onNewKey: () => void;
  onNotice: (title: string) => void;
}) {
  const act = useServerActions(s, on);
  const local = { removing: act.removing, now };
  const state = serverState(s, local);
  const lines = serverLines(s, timeZone, local);
  const guide = serverGuideLine(s, local);
  return (
    <div
      data-server={s.id}
      data-status={s.status}
      className={cardClass(s, Boolean(lines.reason || guide))}
    >
      <AppIcon app={`mcp:${s.name}`} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <ServerName
          server={s}
          local={local}
          wait={s.status === "connecting" && s.expires_at ? lines.under : null}
        />
        {lines.reason ? <p className="text-meta leading-[18px] text-copy">{lines.reason}</p> : null}
        <GuideLine kind={guide} onReview={on.onTools} />
        {manage ? (
          <CardAction
            kind={serverButton(s, local)}
            busy={act.busy}
            onRun={() => act.run("reconnect")}
          />
        ) : null}
        <ServerShare server={s} manage={manage} act={act} at="phone" />
        <ErrorLine error={act.error} />
      </div>
      <ToolsColumn tools={lines.tools} under={lines.under} />
      <StateLabel tone={state.tone} className="hidden w-45 shrink-0 md:flex">
        {act.reading ? "Reading its tools…" : state.text}
      </StateLabel>
      <ServerShare server={s} manage={manage} act={act} at="desktop" />
      {manage ? (
        <ServerMenu server={s} busy={act.busy} removing={act.removing} onRun={act.run} />
      ) : (
        <span className="hidden size-8 shrink-0 md:block" />
      )}
      <RemoveConfirm
        server={s}
        open={act.confirm}
        onOpenChange={act.setConfirm}
        onConfirm={act.doRemove}
      />
    </div>
  );
}

/** A card's frame: centred on a desktop unless it has an extra line, and red when its sign-in ended. */
const cardClass = (s: McpServer, extraLine: boolean) =>
  cn(
    "flex items-start gap-3 rounded-panel bg-card px-4 py-3.5 md:gap-5 md:px-5 md:py-4",
    !extraLine && "md:items-center",
    s.status === "expired"
      ? "shadow-[0_0_0_1px_rgb(162_59_47/0.4)]"
      : "shadow-[0_0_0_1px_rgb(31_42_68/0.08)]",
  );

/** Its guide changed, or couldn't be read (slice 6k §5; Paper 44b, 44c): its tools work either way. */
function GuideLine({
  kind,
  onReview,
}: {
  kind: "changed" | "unreadable" | null;
  onReview: () => void;
}) {
  if (!kind) return null;
  if (kind === "unreadable") {
    return (
      <p data-guide-line={kind} className="pt-1 text-meta leading-[18px] text-muted-foreground">
        Its guide couldn’t be read. Its tools work without it.
      </p>
    );
  }
  return (
    <p
      data-guide-line={kind}
      className="flex min-h-11 items-center gap-1.5 text-meta leading-[18px] text-copper md:min-h-0 md:pt-1"
    >
      Its guide changed.
      <button
        type="button"
        onClick={onReview}
        className="font-medium text-foreground underline underline-offset-2"
      >
        Review it
      </button>
    </p>
  );
}

/**
 * A server's Who can use it dropdown (slice 6a §6.4): under its lines on a phone, before
 * its menu on a desktop (an empty slot there when it can't be shared yet).
 */
function ServerShare({
  server: s,
  manage,
  act,
  at,
}: {
  server: McpServer;
  manage: boolean;
  act: ReturnType<typeof useServerActions>;
  at: "phone" | "desktop";
}) {
  const can = manage && !s.removing && s.status !== "connecting";
  if (!can) return at === "desktop" ? <span className="hidden w-35 shrink-0 md:block" /> : null;
  return (
    <ShareSelect
      at={at}
      shared={s.shared}
      label={s.name}
      reach="with the tools you turned on"
      busy={act.busy}
      onChange={act.doShare}
    />
  );
}

/** A desktop card's tool counts, and when they were read (or what's happening). */
function ToolsColumn({ tools, under }: { tools: string; under: string }) {
  return (
    <div className="hidden w-50 shrink-0 flex-col gap-0.5 md:flex">
      <span className="text-sm text-foreground">{tools}</span>
      {under ? <span className="text-meta text-muted-foreground tabular-nums">{under}</span> : null}
    </div>
  );
}

/**
 * Under the name: Reconnect when its sign-in ended, Try again when a new
 * server's sign-in link may have expired or been closed.
 */
function CardAction({
  kind,
  busy,
  onRun,
}: {
  kind: "reconnect" | "retry" | null;
  busy: boolean;
  onRun: () => void;
}) {
  if (!kind) return null;
  const size = "mt-1 h-10 w-full text-sm md:h-7.5 md:w-fit md:px-3 md:text-meta";
  return kind === "reconnect" ? (
    <Button onClick={onRun} disabled={busy} className={cn(SOLID_RED, size)}>
      Reconnect
    </Button>
  ) : (
    <Button variant="outline" onClick={onRun} disabled={busy} className={cn("bg-card", size)}>
      Try again
    </Button>
  );
}

/**
 * The name, the address and how it signs in; on a phone, its counts and
 * state too (Paper 40b), and an OAuth add's wait for its sign-in.
 */
function ServerName({
  server: s,
  local,
  wait,
}: {
  server: McpServer;
  local: ServerLocal;
  wait: string | null;
}) {
  const state = serverState(s, local);
  return (
    <>
      <span className="text-body font-semibold text-foreground">{s.name}</span>
      <span className="truncate text-meta text-muted-foreground">
        <span className="font-mono text-xs">
          <span className="hidden md:inline">{s.url}</span>
          <span className="md:hidden">{bareUrl(s.url)}</span>
        </span>
        <span className="hidden md:inline"> · {AUTH_WORDS[s.auth]}</span>
      </span>
      {wait ? (
        <span className="text-meta text-muted-foreground tabular-nums md:hidden" data-wait>
          {wait}
        </span>
      ) : null}
      <div className="flex flex-wrap items-center gap-1.5 pt-1 md:hidden">
        <span className="text-meta text-copy">
          {AUTH_WORDS[s.auth]} · {s.tools.on} on, {s.tools.off} off
        </span>
        <span className="text-meta text-hint">·</span>
        <StateLabel tone={state.tone}>{state.text}</StateLabel>
      </div>
    </>
  );
}

const SERVER_ACTIONS: Record<ServerAction, string> = {
  tools: "Tools",
  refresh: "Refresh tools",
  reconnect: "Reconnect",
  remove: "Remove",
};

/**
 * The ⋯ menu, by the server's state (Paper 44). While a change to it runs,
 * only Tools (which changes nothing) can be chosen; while Remove runs, none
 * (Devin on #251).
 */
function ServerMenu({
  server: s,
  busy,
  removing,
  onRun,
}: {
  server: McpServer;
  busy: boolean;
  removing: boolean;
  onRun: (a: ServerAction) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={removing}
        render={
          <Button
            variant="ghost"
            size="icon"
            className="size-9 shrink-0 text-muted-foreground md:size-8"
            aria-label={`${s.name}: more`}
          />
        }
      >
        <Ellipsis className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 p-1.5">
        {serverMenu(s).map((a) => (
          <Fragment key={a}>
            {a === "remove" ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem
              onClick={() => onRun(a)}
              disabled={!serverActionAllowed(a, busy)}
              variant={a === "remove" ? "destructive" : "default"}
              className="font-medium"
            >
              {a === "reconnect" && s.status === "connecting" ? "Try again" : SERVER_ACTIONS[a]}
            </DropdownMenuItem>
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** What Composio forgets on Remove. */
const FORGETS: Record<McpServer["auth"], string> = {
  none: "registration",
  api_key: "key",
  oauth: "sign-in",
};

/** Remove asks once more (Paper 44). */
function RemoveConfirm({
  server: s,
  open,
  onOpenChange,
  onConfirm,
}: {
  server: McpServer;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {s.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            Your crew loses its {plural(s.tools.on + s.tools.off, "tool")}, and Crew forgets its{" "}
            {FORGETS[s.auth]}. To change its URL or how it signs in, remove it and add it again.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep it</AlertDialogCancel>
          <Button
            className={SOLID_RED}
            onClick={() => {
              onOpenChange(false);
              onConfirm();
            }}
          >
            Remove
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
